import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Modal } from "@swot/base-components";

export type CmtsEnterpriseModalProps = {
  open: boolean;
  title: string;
  subtitle?: string | undefined;
  size?: "sm" | "md" | "lg" | "xl" | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
  onClose: () => void;
};

export function CmtsEnterpriseModal(props: CmtsEnterpriseModalProps) {
  useEffect(() => {
    if (!props.open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") props.onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [props.open, props.onClose]);

  if (!props.open) return null;
  const root = typeof document !== "undefined" ? document.body : null;
  if (!root) return null;

  return createPortal(
    <div
      className="cmts-enterprise-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget ? props.onClose() : undefined}
    >
      <section
        className={`cmts-enterprise-modal cmts-enterprise-modal--${props.size ?? "md"}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Modal title={props.title} onClose={props.onClose}>
          {props.subtitle ? <p className="cmts-enterprise-modal__subtitle">{props.subtitle}</p> : null}
          <div className="cmts-enterprise-modal__body">{props.children}</div>
          {props.footer ? <footer className="cmts-enterprise-modal__footer">{props.footer}</footer> : null}
        </Modal>
      </section>
    </div>,
    root,
  );
}
